import { SlackConnectionStatus } from '../../../graphql/enums.graphql';

export type SlackTone = 'ok' | 'warning' | 'danger' | 'neutral';

export interface SlackStatusCopy {
  label: string;
  tone: SlackTone;
  /** What is actually happening, in words an admin can act on. */
  explanation: string;
  /** The button that fixes it, if one does. */
  action?: 'connect' | 'reconnect';
}

/**
 * One sentence per connection state.
 *
 * Five failure states rather than a boolean, because they have different
 * remedies and an admin who reads "not working" cannot tell whether to
 * reconnect, reinstall, or simply wait. This map is the only place that
 * decision is expressed, so the banner and the settings page cannot disagree
 * about what a state means.
 */
export const SLACK_STATUS_COPY: Record<SlackConnectionStatus, SlackStatusCopy> = {
  [SlackConnectionStatus.Connected]: {
    label: 'Connected',
    tone: 'ok',
    explanation: 'Revvly can send messages to this workspace.',
  },
  [SlackConnectionStatus.Degraded]: {
    label: 'Degraded',
    tone: 'warning',
    explanation:
      'Slack could not be reached on the last check. Messages are still being attempted and will retry.',
  },
  [SlackConnectionStatus.Revoked]: {
    label: 'Needs reconnecting',
    tone: 'danger',
    explanation:
      'Slack no longer accepts the stored token. Nothing is being delivered until an administrator reconnects.',
    action: 'reconnect',
  },
  [SlackConnectionStatus.Uninstalled]: {
    label: 'App removed',
    tone: 'danger',
    explanation:
      'The Revvly app was removed from this workspace. Reinstalling it restores delivery; member links are kept.',
    action: 'reconnect',
  },
  [SlackConnectionStatus.Disconnected]: {
    label: 'Not connected',
    tone: 'neutral',
    explanation: 'Connect a Slack workspace to send check-in prompts and cycle announcements.',
    action: 'connect',
  },
};

/**
 * What the `?result=` on the OAuth return means.
 *
 * Every way the install can end is nameable, because a flow that returns
 * "something went wrong" is one an admin cannot act on. Keys match
 * `SlackConnectResult` on the backend.
 */
export const SLACK_CONNECT_RESULT_COPY: Record<string, { tone: SlackTone; message: string }> = {
  success: {
    tone: 'ok',
    message: 'Slack is connected. Two steps left: map people, pick a channel.',
  },
  cancelled: {
    tone: 'neutral',
    message: 'You cancelled the Slack connection. Nothing was changed.',
  },
  insufficient_permissions: {
    tone: 'danger',
    message:
      'Slack refused the install. Ask a Slack workspace administrator to connect Revvly, or to grant you permission to install apps.',
  },
  wrong_workspace: {
    tone: 'danger',
    message:
      'That Slack workspace is already connected to another Revvly account. Disconnect it there first, or connect a different workspace.',
  },
  exchange_failed: {
    tone: 'danger',
    message: 'Slack did not complete the handshake. Try connecting again.',
  },
  invalid_state: {
    tone: 'danger',
    message: 'That connection link had expired. Start the connection again from this page.',
  },
};

/** Whether this state means messages are being attempted at all. */
export function isSendable(status: SlackConnectionStatus): boolean {
  return status === SlackConnectionStatus.Connected || status === SlackConnectionStatus.Degraded;
}
